import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useReducedMotion } from 'framer-motion';
import { SiteHeader } from '@/components/layout/SiteHeader';
import { SiteFooter } from '@/components/layout/SiteFooter';
import { CurrencySwitcher } from '@/components/layout/CurrencySwitcher';
import { Reveal, Stagger, StaggerItem } from '@/components/landing/kit';
import { billingApi } from '@/lib/billing';
import { useDocumentMeta } from '@/lib/useDocumentMeta';
import {
  currentDisplayCurrency,
  fromUsd,
  formatDisplayCurrency,
} from '@/lib/displayCurrency';

type PlanKey = 'free' | 'growth' | 'enterprise';
type PayableKey = Exclude<PlanKey, 'free'>;
type BillingInterval = 'monthly' | 'annual';

interface Plan {
  key: PlanKey;
  name: string;
  monthlyUsdPerSeat: number;
  perks: string[];
  /** i18n key for this tier's full feature array (string[] via returnObjects). */
  featuresKey: string;
  cta: string;
  highlight: boolean;
}

/**
 * Effective annual discount we show on the cards — Stripe yearly
 * prices are set to monthly × 10 = 2 months free ≈ 16.7% off. Used
 * only when the live catalogue hasn't loaded yet (so the page can
 * render a sensible math model before /billing/plans returns).
 */
const ANNUAL_DISCOUNT = 1 - 10 / 12;

/**
 * Loglass's published mid-enterprise band is roughly ¥1.5M-¥6M+/year.
 * We use ¥6M as the conservative comparison point — that's where they
 * typically land for a 30-50 person finance team. At ¥150/USD that's
 * about $40,000/year, the savings annotation works off this.
 */
const LOGLASS_TYPICAL_USD_PER_YEAR = 40_000;

export function PricingPage() {
  const { t, i18n } = useTranslation();
  const [currency, setCurrency] = useState(currentDisplayCurrency());
  useDocumentMeta({
    title: 'Pricing — CadencEZ',
    description: 'Public per-seat pricing for CadencEZ. Free, Growth ($29), Enterprise ($79). AI usage at passthrough cost; no markup.',
    path: '/pricing',
  });
  const [seats, setSeats] = useState<number>(20);
  // Calculator pill is for paid plans only. Free is shown as a card below
  // (informational) but isn't a "buyable" option in the pricing math — the
  // user already gets it by signing up.
  const [planKey, setPlanKey] = useState<PayableKey>('growth');
  const [interval, setInterval] = useState<BillingInterval>('annual');

  // Stripe-sourced live plan catalogue. Single source of truth for
  // amounts — a Stripe Dashboard price edit propagates within the
  // backend cache TTL (~10 min) with zero frontend code changes.
  const plansQ = useQuery({ queryKey: ['billing-plans'], queryFn: () => billingApi.plans(), staleTime: 5 * 60 * 1000 });
  const priceFor = (code: PlanKey): number => {
    const p = plansQ.data?.find((x) => x.code === code);
    return p ? p.monthlyUnitAmountCents / 100 : 0;
  };

  const plans: Plan[] = [
    {
      key: 'free',
      name: t('pricing.free'),
      monthlyUsdPerSeat: priceFor('free'),
      perks: [t('pricing.free_seats'), t('pricing.free_sources'), t('pricing.free_history')],
      featuresKey: 'pricing.free_features',
      cta: t('pricing.cta_start_free', { defaultValue: 'Start free' }),
      highlight: false,
    },
    {
      key: 'growth',
      name: t('pricing.growth'),
      monthlyUsdPerSeat: priceFor('growth'),
      perks: [t('pricing.growth_perk')],
      featuresKey: 'pricing.growth_features',
      cta: t('pricing.cta_start_growth', { defaultValue: 'Start Growth' }),
      highlight: true,
    },
    {
      key: 'enterprise',
      name: t('pricing.enterprise'),
      monthlyUsdPerSeat: priceFor('enterprise'),
      perks: [t('pricing.enterprise_perk')],
      featuresKey: 'pricing.enterprise_features',
      cta: t('pricing.cta_try_enterprise', { defaultValue: 'Try Enterprise' }),
      highlight: false,
    },
  ];

  return (
    <div className="min-h-screen">
      <SiteHeader />
      <main className="mx-auto max-w-6xl px-6 pt-20 pb-24">
        {/* PAGE HEADER — editorial entrance cascade */}
        <Stagger slow className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <StaggerItem
              as="h1"
              className="display text-4xl md:text-5xl font-semibold tracking-tight text-ink-900 serif-jp"
            >
              {t('pricing.title')}
            </StaggerItem>
            <StaggerItem as="p" className="mt-3 text-ink-600 max-w-2xl">
              {t('pricing.intro')}
            </StaggerItem>
          </div>
          <StaggerItem className="shrink-0">
            <CurrencySwitcher onChange={setCurrency} />
          </StaggerItem>
        </Stagger>

        {/* CALCULATOR — reveals on scroll */}
        <Reveal variant="fadeInUp" delay={0.1}>
          <Calculator
            seats={seats}
            setSeats={setSeats}
            planKey={planKey}
            setPlanKey={setPlanKey}
            interval={interval}
            setInterval={setInterval}
            displayCurrency={currency}
            locale={i18n.language}
            listPerSeatUsd={priceFor(planKey)}
          />
        </Reveal>

        {/* PLAN CARDS — cascade in, one lit system per card */}
        <Stagger className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
          {plans.map((p) => (
            <StaggerItem
              key={p.key}
              className={
                'flex transition-[transform,box-shadow] duration-200 ' +
                (p.key === 'free'
                  ? ''
                  : 'hover:-translate-y-px hover:shadow-soft')
              }
            >
              <PlanCard
                plan={p}
                displayCurrency={currency}
                locale={i18n.language}
                selected={p.key !== 'free' && p.key === planKey}
                selectable={p.key !== 'free'}
                interval={interval}
                onSelect={() => p.key !== 'free' && setPlanKey(p.key as PayableKey)}
              />
            </StaggerItem>
          ))}
        </Stagger>

        <Reveal variant="fadeIn" delay={0.1}>
          <p className="mt-8 text-xs text-ink-500 max-w-2xl">
            {t('pricing.fine_print')}
          </p>
        </Reveal>
      </main>
      <SiteFooter />
    </div>
  );
}

/**
 * Live pricing calculator. The spec at IMPLEMENTATION.md §3A calls the
 * pricing page "the page is the pricer" — this is the component that
 * delivers on it. Slider drives seats; pill drives plan; everything
 * else recomputes inline including a savings annotation vs Loglass's
 * published band.
 */
function Calculator({
  seats,
  setSeats,
  planKey,
  setPlanKey,
  interval,
  setInterval,
  displayCurrency,
  locale,
  listPerSeatUsd,
}: {
  seats: number;
  setSeats: (n: number) => void;
  planKey: PayableKey;
  setPlanKey: (p: PayableKey) => void;
  interval: BillingInterval;
  setInterval: (i: BillingInterval) => void;
  displayCurrency: string;
  locale: string;
  /** Stripe-sourced per-seat USD list price for the chosen tier. */
  listPerSeatUsd: number;
}) {
  const { t } = useTranslation();
  // Annual is displayed as a discounted *effective monthly rate*. The
  // headline number stays a per-month figure so the seat math reads
  // consistently across both intervals. Yearly total is shown below it.
  const effectivePerSeatUsd = interval === 'annual'
    ? listPerSeatUsd * (1 - ANNUAL_DISCOUNT)
    : listPerSeatUsd;
  const monthlyUsd = seats * effectivePerSeatUsd;
  const annualUsd = monthlyUsd * 12;

  // Converted display amount drives the count-up; formatting is applied
  // by the formatter so the currency symbol/locale stay correct.
  const monthlyDisplayAmount = useMemo(
    () => fromUsd(monthlyUsd, displayCurrency).amount,
    [monthlyUsd, displayCurrency],
  );
  const annualDisplay = useMemo(
    () => formatDisplayCurrency(fromUsd(annualUsd, displayCurrency).amount, displayCurrency, locale),
    [annualUsd, displayCurrency, locale],
  );

  const savingsPct = annualUsd > 0 && annualUsd < LOGLASS_TYPICAL_USD_PER_YEAR
    ? Math.round(((LOGLASS_TYPICAL_USD_PER_YEAR - annualUsd) / LOGLASS_TYPICAL_USD_PER_YEAR) * 100)
    : null;

  return (
    <section className="mt-10 rounded-2xl border border-border bg-card p-6 sm:p-8">
      <div className="flex items-baseline justify-between gap-4 mb-6">
        <h2 className="display text-xl font-semibold text-ink-900">{t('pricing.calculator_title')}</h2>
        <span className="num text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
          {t('pricing.calculator_chosen')}: {t(`pricing.${planKey}`)}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-center">
        {/* Seats slider — 1..500 */}
        <div className="lg:col-span-3">
          <label className="block">
            <div className="flex items-baseline justify-between mb-2">
              <span className="text-xs uppercase tracking-[0.14em] text-muted-foreground font-medium">
                {t('pricing.calculator_seats')}
              </span>
              <span className="num text-[13px] font-semibold text-ink-900">
                {t('pricing.calculator_seats_unit', { count: seats })}
              </span>
            </div>
            <input
              type="range"
              min={1}
              max={500}
              step={1}
              value={seats}
              onChange={(e) => setSeats(Number(e.target.value))}
              className="w-full accent-emerald-600 cursor-pointer"
              aria-label={t('pricing.calculator_seats')}
            />
            <div className="mt-1.5 flex justify-between text-[10px] text-muted-foreground num">
              <span>1</span><span>100</span><span>250</span><span>500</span>
            </div>
          </label>

          {/* Plan pill toggle — paid tiers only. Free is excluded because
              it isn't billable; users get it by signing up. */}
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <div className="inline-flex rounded-lg border border-border p-0.5 bg-background">
              {(['growth','enterprise'] as PayableKey[]).map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setPlanKey(k)}
                  className={
                    'px-3.5 py-1.5 text-xs font-semibold rounded-md transition-colors ' +
                    (k === planKey
                      ? 'bg-foreground text-background'
                      : 'text-muted-foreground hover:text-foreground')
                  }
                >
                  {t(`pricing.${k}`)}
                </button>
              ))}
            </div>

            {/* Billing interval pill */}
            <div className="inline-flex items-center rounded-lg border border-border p-0.5 bg-background">
              {(['monthly','annual'] as BillingInterval[]).map((iv) => (
                <button
                  key={iv}
                  type="button"
                  onClick={() => setInterval(iv)}
                  className={
                    'px-3.5 py-1.5 text-xs font-semibold rounded-md transition-colors inline-flex items-center gap-1.5 ' +
                    (iv === interval
                      ? 'bg-foreground text-background'
                      : 'text-muted-foreground hover:text-foreground')
                  }
                >
                  {t(`pricing.billing_${iv}`)}
                  {iv === 'annual' && (
                    <span className={
                      'text-[10px] font-bold rounded-sm px-1 py-px ' +
                      (interval === 'annual' ? 'bg-emerald-400/20 text-emerald-300' : 'bg-emerald-100 text-emerald-700')
                    }>
                      −{Math.round(ANNUAL_DISCOUNT * 100)}%
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Live total */}
        <div className="lg:col-span-2 lg:border-l lg:border-border lg:pl-6">
          <LiveTotal
            amount={monthlyDisplayAmount}
            currency={displayCurrency}
            locale={locale}
            className="num text-4xl sm:text-5xl font-semibold text-ink-900 tabular-nums"
          />
          <p className="text-xs text-muted-foreground mt-0.5">
            {t('pricing.calculator_monthly')}
          </p>
          <p className="num text-[13px] text-ink-700 mt-3 tabular-nums">
            {t('pricing.calculator_annual', { amount: annualDisplay })}
          </p>
          <p className="num text-[11px] text-muted-foreground mt-1">
            {interval === 'annual'
              ? t('pricing.billed_annually')
              : t('pricing.billed_monthly')}
          </p>
          {savingsPct !== null && savingsPct >= 10 && (
            <p className="mt-3 inline-flex items-center rounded-md bg-emerald-50 px-2 py-1 text-[11px] font-semibold text-emerald-700 border border-emerald-200">
              {t('pricing.calculator_vs_loglass', { pct: savingsPct })}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}

/**
 * The live computed total. Editorial count-up on first view (mirrors the
 * landing's AnimatedNumber easing/duration), then re-counts smoothly when
 * the figure changes — slider/interval/plan/currency edits all flow
 * through `amount`. The currency symbol & locale formatting are applied
 * by formatDisplayCurrency on every frame so they always stay correct.
 *
 * A local primitive (vs the shared kit AnimatedNumber) because the kit's
 * count-up fires once on inView and then freezes; this page needs the
 * number to keep tracking the live calculator value after that first reveal.
 * Honors prefers-reduced-motion by snapping to the exact value instantly.
 */
function LiveTotal({
  amount,
  currency,
  locale,
  className,
}: {
  amount: number;
  currency: string;
  locale: string;
  className?: string;
}) {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLParagraphElement | null>(null);
  const inView = useRef(false);
  const fromRef = useRef(0);
  const rafRef = useRef(0);
  const [display, setDisplay] = useState(reduce ? amount : 0);

  // Fire (and re-fire) the count-up whenever the target value changes,
  // but only once the element has scrolled into view.
  useEffect(() => {
    if (reduce) {
      setDisplay(amount);
      return;
    }
    const el = ref.current;
    if (!el) return;

    const run = () => {
      const from = fromRef.current;
      const to = amount;
      if (from === to) {
        setDisplay(to);
        return;
      }
      const duration = 900;
      const start = performance.now();
      const step = (now: number) => {
        const t = Math.min(1, (now - start) / duration);
        const eased = 1 - Math.pow(1 - t, 3);
        const current = from + (to - from) * eased;
        setDisplay(current);
        if (t < 1) {
          rafRef.current = requestAnimationFrame(step);
        } else {
          setDisplay(to);
          fromRef.current = to;
        }
      };
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(step);
    };

    if (inView.current) {
      run();
      return () => cancelAnimationFrame(rafRef.current);
    }

    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && !inView.current) {
            inView.current = true;
            run();
          }
        }
      },
      { threshold: 0.4 },
    );
    io.observe(el);
    return () => {
      io.disconnect();
      cancelAnimationFrame(rafRef.current);
    };
  }, [amount, reduce]);

  return (
    <p ref={ref} className={className}>
      {formatDisplayCurrency(display, currency, locale)}
    </p>
  );
}

/** How many features to show before the card collapses the rest. */
const FEATURE_PREVIEW_COUNT = 6;

/**
 * Per-plan feature list in the editorial emerald/ink style (mirrors the
 * landing PricingSection check rows). Pulls the tier's full feature array
 * from i18n via t(key, {returnObjects:true}); shows the first
 * FEATURE_PREVIEW_COUNT items, then a "Show all N features" / "Show less"
 * toggle reveals the rest. Guards for non-array i18n values so a missing
 * or mistyped key degrades to an empty list instead of throwing.
 */
function FeatureList({
  featuresKey,
  className,
}: {
  featuresKey: string;
  className?: string;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const raw = t(featuresKey, { returnObjects: true }) as unknown;
  const features = Array.isArray(raw) ? (raw as string[]).filter((f) => typeof f === 'string') : [];

  if (features.length === 0) return <div className={className} />;

  const hasMore = features.length > FEATURE_PREVIEW_COUNT;
  const visible = expanded || !hasMore ? features : features.slice(0, FEATURE_PREVIEW_COUNT);

  return (
    <div className={className}>
      <ul className="space-y-2 text-sm text-ink-600">
        {visible.map((feature) => (
          <li key={feature} className="flex gap-2.5">
            <Check className="h-4 w-4 mt-0.5 shrink-0 text-emerald-600" strokeWidth={2.4} />
            <span>{feature}</span>
          </li>
        ))}
      </ul>
      {hasMore && (
        <button
          type="button"
          onClick={(e) => {
            // The whole card is clickable to sync the calculator; keep the
            // toggle from also triggering card selection.
            e.stopPropagation();
            setExpanded((v) => !v);
          }}
          className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:text-emerald-800 transition-colors"
          aria-expanded={expanded}
        >
          {expanded
            ? t('pricing.show_less_features', { defaultValue: 'Show less' })
            : t('pricing.show_all_features', {
                count: features.length,
                defaultValue: 'Show all {{count}} features',
              })}
          <ChevronDown
            className={'h-3.5 w-3.5 transition-transform ' + (expanded ? 'rotate-180' : '')}
            strokeWidth={2.4}
          />
        </button>
      )}
    </div>
  );
}

function PlanCard({
  plan,
  displayCurrency,
  locale,
  selected,
  selectable,
  interval,
  onSelect,
}: {
  plan: Plan;
  displayCurrency: string;
  locale: string;
  selected: boolean;
  selectable: boolean;
  interval: BillingInterval;
  onSelect: () => void;
}) {
  const { t } = useTranslation();
  // Paid cards reflect the chosen billing interval; Free has no concept of
  // a billing interval, so it always shows its zero price as-is.
  const effectiveUsd = plan.monthlyUsdPerSeat === 0
    ? 0
    : (interval === 'annual'
        ? plan.monthlyUsdPerSeat * (1 - ANNUAL_DISCOUNT)
        : plan.monthlyUsdPerSeat);
  const converted = fromUsd(effectiveUsd, displayCurrency);
  const priceLabel = effectiveUsd === 0
    ? formatDisplayCurrency(0, displayCurrency, locale)
    : formatDisplayCurrency(converted.amount, displayCurrency, locale);

  // Each card is a clickable container so clicking anywhere syncs the
  // calculator at the top; the bottom CTA is a real <Link> so the user
  // can sign up directly. `stopPropagation` on the link keeps the card
  // selection from re-firing when only the CTA is clicked.
  const containerClass =
    'text-left rounded-2xl border bg-white p-6 flex flex-col flex-1 w-full transition-colors ' +
    (!selectable
      ? 'border-ink-200 cursor-default opacity-90'
      : selected
        ? 'border-emerald-600 ring-1 ring-emerald-600/30 cursor-pointer'
        : plan.highlight
          ? 'border-accent-500 hover:border-emerald-600 cursor-pointer'
          : 'border-ink-200 hover:border-ink-400 cursor-pointer');

  const signupHref = plan.key === 'free'
    ? '/signup'
    : `/signup?plan=${plan.key}&interval=${interval}`;

  return (
    <div
      role={selectable ? 'button' : undefined}
      tabIndex={selectable ? 0 : -1}
      onClick={selectable ? onSelect : undefined}
      onKeyDown={(e) => {
        if (!selectable) return;
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(); }
      }}
      className={containerClass}
    >
      <h3 className="text-lg font-semibold text-ink-900">{plan.name}</h3>
      <p className="mt-3 num text-3xl font-semibold text-ink-900 tabular-nums">{priceLabel}</p>
      <p className="text-xs text-ink-500 mt-0.5">
        {effectiveUsd === 0
          ? t('pricing.forever_free')
          : interval === 'annual'
            ? t('pricing.per_seat_month_annual')
            : t('pricing.per_seat_month')}
      </p>
      <FeatureList featuresKey={plan.featuresKey} className="mt-4 flex-1" />
      <Link
        to={signupHref}
        onClick={(e) => e.stopPropagation()}
        className={
          'mt-6 inline-flex items-center justify-center px-4 py-2.5 rounded-lg text-sm font-semibold transition ' +
          (plan.highlight
            ? 'bg-accent-500 text-white hover:bg-accent-600'
            : 'border border-ink-300 text-ink-900 hover:bg-ink-50')
        }
      >
        {plan.cta}
      </Link>
    </div>
  );
}
